import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { useRecoilState, useSetRecoilState } from "recoil";

import { PropertyCategory, PropertyTemporary, PropertyType } from "@/api/generated";
import { Loading } from "@/components/Loading";
import {
  focusRegistrationField,
  getRegistrationFieldStep,
} from "@/components/property_registration/RegistrationActions";
import { PropertyRegistrationMode } from "@/components/property_registration/RegistrationContext";
import { DraftPropertyModal } from "@/components/property_registration/RegistrationDraft";
import { DraftPropertyReuseModal } from "@/components/property_registration/RegistrationDraft";
import { RegistrationLayout, RegistrationMethods } from "@/components/property_registration/RegistrationLayout";
import { RegistrationReview } from "@/components/property_registration/RegistrationReview";
import { getRegistrationStepFields } from "@/components/property_registration/registrationStepFields";
import { useRegistrationExit } from "@/components/property_registration/useRegistrationExit";
import { DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA, DateFieldNames, DoubleFieldNames } from "@/constants/addNewProperty";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { castFieldInputs } from "@/utilities/castFieldInputs";
import { deleteUnacceptableKeys } from "@/utilities/deleteUnacceptableKeys";
import {
  formDataMappingToLeaseContract,
  formDataMappingToLeaseContractForUpdate,
  formDataMappingToProperty,
  formDataMappingToPropertyBuildingRoom,
  handleFirstRegistration,
  postProperty,
} from "@/utilities/propertyManagement";
import {
  getStorageConsiderationPropertyRegistrationState,
  setStorageConsiderationPropertyRegistrationState,
  removeStorageConsiderationPropertyRegistrationState,
} from "@/utilities/storage";
import { considerationPropertySchema, ConsiderationPropertyFormData } from "@/validations/management/registration";
import {
  ConsiderationPropertyAiInputUsedState,
  ConsiderationPropertyRegistrationState,
  PropertyManagementListSnackbarState,
  UserDataFirstRegisteredStatusState,
  useRequestInitialLoading,
} from "@/view_models/atoms";

import AddNewConsiderationPropertyBasicPage from "./components/Basic";
import AddNewConsiderationPropertyCashFlowPage from "./components/Cashflow";
import { ConsiderationRegistrationNotice } from "./components/ConsiderationRegistrationNotice";
import AddNewConsiderationPropertyLoanPage from "./components/Loan";

export const ManagementAddNewConsiderationPropertyPage: React.FC = () => {
  const isSubmittingRef = React.useRef(false);
  const history = useHistory();
  const exitRegistration = useRegistrationExit("/management/consideration");
  const methods = useForm<ConsiderationPropertyFormData>({
    mode: "onTouched",
    shouldUnregister: false,
    // エラー項目への移動はonInvalidで行い、閉じたシートのrefへのフォーカスを防ぐ。
    shouldFocusError: false,
    resolver: zodResolver(considerationPropertySchema),
  });

  const [currentStep, setCurrentStep] = React.useState(0);
  const [isAiReview, setIsAiReview] = React.useState(false);
  const [registrationMode, setRegistrationMode] = React.useState<PropertyRegistrationMode | null>(null);

  const requestInitialLoading = useRequestInitialLoading();
  // 物件一覧ページでのスナックバー表示用
  const setManagementListSnackBarState = useSetRecoilState(PropertyManagementListSnackbarState);

  const [isOpenReuseTempoModal, setIsOpenReuseTempoModal] = React.useState(false);
  const [isClickedPageBack, setIsClickedPageBack] = React.useState(false);
  const setUserDataFirstRegisteredStatus = useSetRecoilState(UserDataFirstRegisteredStatusState);
  // LocalStorage
  const [formPersistData, setFormPersistData] = useRecoilState(ConsiderationPropertyRegistrationState);
  const [, setAiInputUsed] = useRecoilState(ConsiderationPropertyAiInputUsedState);
  const localStorageFormData = getStorageConsiderationPropertyRegistrationState();
  // 一時保存情報の状態
  const [propertyTempInfo, setPropertyTempInfo] = React.useState<PropertyTemporary>(null);

  const onClickPageBack = React.useCallback(() => {
    window.scrollTo(0, 0);
    setIsClickedPageBack(true);
  }, [setIsClickedPageBack]);

  const onClickPageNext = React.useCallback(() => {
    window.scrollTo(0, 0);
    setCurrentStep((prev) => prev + 1);
  }, [setCurrentStep]);

  /**
   * 関数定義
   */

  // 一時保存関数
  const saveTemporary = async () => {
    await methods.trigger();

    // DBに一時保存データがあるかどうかフラグを保存
    const fp = PropallyApiFactory();
    fp.v1PropertyTemporaryPost({ withCredentials: true }).then(() => {
      console.log("一時保存しました");
    });
    setFormPersistData({ ...formPersistData, ...methods.getValues() });
  };
  // ==== 一時保存情報の利用 ====
  const onClickReuseTempo = async () => {
    setIsOpenReuseTempoModal(false);
  };

  // ==== 物件一時保存情報削除 ====
  const deletePropertyTemporary = async () => {
    const fp = PropallyApiFactory();
    try {
      const response = await fp.v1PropertyTemporaryDelete({ withCredentials: true });
      if (response.status === 200) {
        // フォームのデフォルト値を削除
        setFormPersistData(DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA);
        // ローカルストレージの削除
        removeStorageConsiderationPropertyRegistrationState();
        // フォームの値をリセット
        methods.reset(DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA);
        setAiInputUsed(false);
        // 一時保存情報の削除
        setPropertyTempInfo(null);
      }
    } catch (e) {
      console.error(e);
    }
    setIsOpenReuseTempoModal(false);
  };

  const saveTemporaryAndGoBack = async () => {
    await saveTemporary();
    history.push("/management/consideration");
  };
  const cancelSaveTemporaryAndGoBack = async () => {
    await deletePropertyTemporary();
    history.push("/management/consideration");
  };

  // 各ページでの処理
  const validate = async () => {
    const isValid = await methods.trigger(getRegistrationStepFields("consideration", currentStep));
    if (isValid) {
      const saveFormData = {
        ...formPersistData,
        ...methods.getValues(),
      };
      console.log("saveFormData", saveFormData);
      console.log("image", saveFormData.property_image);
      // LocalStorage
      setFormPersistData(saveFormData);
      setStorageConsiderationPropertyRegistrationState(saveFormData);
      console.log("valid");
    }
    return isValid;
  };
  const onSubmit = React.useCallback(async () => {
    console.log("submit");
    // 二重送信防止
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    // LocalStorage
    setFormPersistData(formPersistData);

    console.log("persistData on submit", formPersistData);

    console.log("methods.getValues()", methods.getValues());
    let data = {
      ...methods.getValues(),
    };
    console.log("data", data);

    // cut off the undefined/non-entered values
    data = deleteUnacceptableKeys(data);

    // cast date & double fields
    data = castFieldInputs(data, { dateFieldNames: DateFieldNames, doubleFieldNames: DoubleFieldNames });
    console.log("processd data", data);

    // Property型に変換
    const property = formDataMappingToProperty(data, PropertyType.NUMBER_1, PropertyCategory.区分マンション);

    console.log("property", property);

    // LeaseContract型に変換
    const lease_contract = formDataMappingToLeaseContract(data);

    // PropertyBuildingRoom型に変換
    const building_room = formDataMappingToPropertyBuildingRoom(data);

    // PropertyBuilder型に変換
    const property_builder = formDataMappingToLeaseContractForUpdate(
      data,
      property,
      lease_contract,
      building_room,
      PropertyCategory.区分マンション
    );

    try {
      const postResponse = await postProperty(property_builder);
      console.log("postResponse", postResponse);

      await handleFirstRegistration(setUserDataFirstRegisteredStatus);
    } catch (e) {
      console.error(e);
      // 物件一覧ページでのスナックバー表示用
      setManagementListSnackBarState({ isOpen: true, message: "物件を追加できませんでした", status: "error" });
      history.push("/management/consideration");
      return;
    }
    // データの初期化
    setFormPersistData(DEFAULT_ADD_NEW_PROPERTY_FORM_SCHEMA);
    setAiInputUsed(false);
    // ローカルストレージの物件情報の削除
    removeStorageConsiderationPropertyRegistrationState();
    console.log("success");
    // 物件一覧ページでのスナックバー表示用
    setManagementListSnackBarState({ isOpen: true, message: "物件を追加しました", status: "success" });
    requestInitialLoading();
    sendEventToAppMeasurementTool(AppEventTokenEnum.ConsiderationPropertyCompleted);
    history.push("/management/consideration");

    isSubmittingRef.current = false; // 送信完了後、二重送信管理フラグをリセット
    console.log("complete");
  }, [formPersistData]);

  const onInvalid = (errors: Record<string, unknown>) => {
    const field = Object.keys(errors)[0];
    if (!field) return;
    setIsAiReview(false);
    setRegistrationMode("manual");
    setCurrentStep(getRegistrationFieldStep("consideration", field));
    focusRegistrationField(field);
  };

  const steps = [
    <AddNewConsiderationPropertyBasicPage
      key="basic"
      onNext={onClickPageNext}
      validate={validate}
      registrationMode={registrationMode || undefined}
      onAiReview={() => {
        setIsAiReview(true);
        window.scrollTo(0, 0);
      }}
    />,
    <AddNewConsiderationPropertyLoanPage
      key="loan"
      onNext={onClickPageNext}
      onBack={() => setCurrentStep((prev) => prev - 1)}
      validate={validate}
    />,
    <AddNewConsiderationPropertyCashFlowPage
      key="cashflow"
      onBack={() => setCurrentStep((prev) => prev - 1)}
      validate={validate}
    />,
  ];
  const stepText = React.useMemo(
    () => [
      {
        title: "物件の概要",
        message: "販売図面や物件概要書を参考にしてください。",
      },
      {
        title: "価格とローン",
        message: "売買契約書や返済予定表を参考にしてください。",
      },
      {
        title: "買った場合の毎月の収支",
        message: "販売図面や募集条件を参考に、想定で入力してください。",
      },
    ],
    []
  );

  // ローカルストレージのデータがある場合は確認モーダルの表示
  React.useEffect(() => {
    const fp = PropallyApiFactory();
    const fetchPropertyRegistrationTemporary = async () => {
      try {
        const response = await fp.v1PropertyTemporaryGet({ withCredentials: true });
        if (response.status === 200) {
          if (response.data?.updated_at && localStorageFormData !== null) {
            setIsOpenReuseTempoModal(true);
            setPropertyTempInfo(response.data);
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchPropertyRegistrationTemporary();
  }, []);

  // ローカルストレージのデータをフォームにセットしてレンダリングをする前にモーダルで確認
  if (isOpenReuseTempoModal) {
    return (
      <RegistrationLayout flow="consideration" onBack={onClickPageBack} title="物件の概要">
        <Loading />
        <DraftPropertyReuseModal
          onClose={() => setIsOpenReuseTempoModal(false)}
          onClickUse={async () => {
            setRegistrationMode("manual");
            await onClickReuseTempo();
          }}
          onClickDelete={deletePropertyTemporary}
          lastSavedDate={dayjs(propertyTempInfo?.updated_at).format("YYYY年MM月DD日 HH:mm")}
        />
      </RegistrationLayout>
    );
  }

  if (currentStep === 0 && registrationMode === null) {
    return (
      <RegistrationLayout
        flow="consideration"
        onBack={exitRegistration}
        title="登録方法の選択"
        description="登録した情報は、あとから変更できます。"
        notice={<ConsiderationRegistrationNotice />}
      >
        <RegistrationMethods onSelect={setRegistrationMode} />
      </RegistrationLayout>
    );
  }

  if (isAiReview) {
    return (
      <RegistrationLayout flow="consideration" onBack={onClickPageBack} title="AIの入力を確認" review>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
            <div hidden aria-hidden="true">
              {steps.slice(1)}
            </div>
            <RegistrationReview
              consideration
              onEdit={(step) => {
                setIsAiReview(false);
                setRegistrationMode("manual");
                setCurrentStep(step);
                window.scrollTo(0, 0);
              }}
            />
          </form>
        </FormProvider>
        {isClickedPageBack && (
          <DraftPropertyModal
            onClose={() => setIsClickedPageBack(false)}
            onClickSave={saveTemporaryAndGoBack}
            onClickCancel={cancelSaveTemporaryAndGoBack}
          />
        )}
      </RegistrationLayout>
    );
  }

  return (
    <RegistrationLayout
      flow="consideration"
      onBack={onClickPageBack}
      step={registrationMode === "ai" && currentStep === 0 ? undefined : currentStep}
      title={
        currentStep === 0 && registrationMode === "ai" ? "物件について教えてください" : stepText[currentStep].title
      }
      description={stepText[currentStep].message}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>{steps[currentStep]}</form>
      </FormProvider>
      {isClickedPageBack ? (
        <DraftPropertyModal
          onClose={() => setIsClickedPageBack(false)}
          onClickSave={saveTemporaryAndGoBack}
          onClickCancel={cancelSaveTemporaryAndGoBack}
        />
      ) : null}
    </RegistrationLayout>
  );
};
